import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Camera, Database, ExternalLink } from "lucide-react";
import type { FyndanuListing } from "@/lib/fyndanu.functions";
import type { KbBlock } from "@/lib/kb-blocks";
import { vehicleSlug } from "@/lib/vehicle-slug";
import { BlogTcoCalculator } from "@/components/BlogTcoCalculator";
import { KbMonthlyCalc, KbPayloadCalc } from "@/components/kb/KbCalculators";

const kr = (n: number | null) =>
  n && n > 0 ? `${n.toLocaleString("sv-SE")} kr` : "Kontakta oss";

function Frame({
  eyebrow,
  title,
  children,
  footnote,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
  footnote?: string;
}) {
  return (
    <section className="not-prose my-10 rounded-3xl border border-border bg-card p-5 md:p-7">
      <p className="eyebrow">{eyebrow}</p>
      <h3 className="mt-1 font-display text-xl md:text-2xl">{title}</h3>
      <div className="mt-5">{children}</div>
      {footnote && <p className="mt-4 text-xs text-muted-foreground">{footnote}</p>}
    </section>
  );
}

/** Prisjämförelse ur vårt eget lager — inget hämtat, inget hittat på. */
function PriceCompare({ listings }: { listings: FyndanuListing[] }) {
  const rows = listings
    .filter((l) => l.price && l.price > 20000)
    .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
    .slice(0, 5);
  if (rows.length < 3) return null;

  return (
    <Frame
      eyebrow="Våra egna siffror"
      title="Prisjämförelse ur vårt lager just nu"
      footnote="Tabellen byggs av vårt faktiska lager och uppdateras automatiskt när priser eller fordon ändras."
    >
      <div className="-mx-5 overflow-x-auto md:mx-0">
        <table className="w-full min-w-[560px] border-collapse text-sm">
          <thead className="bg-secondary/60 text-left">
            <tr>
              <th className="px-4 py-3 font-display text-xs uppercase tracking-wider">Fordon</th>
              <th className="px-4 py-3 font-display text-xs uppercase tracking-wider">År</th>
              <th className="px-4 py-3 font-display text-xs uppercase tracking-wider">Längd</th>
              <th className="px-4 py-3 font-display text-xs uppercase tracking-wider">Bäddar</th>
              <th className="px-4 py-3 font-display text-xs uppercase tracking-wider">Pris</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="border-b border-border/60">
                <td className="px-4 py-3">
                  <Link to="/fordon/$id" params={{ id: vehicleSlug(l) }} className="hover:text-accent">
                    {l.title}
                    <ArrowUpRight size={13} className="ml-1 inline" />
                  </Link>
                </td>
                <td className="px-4 py-3">{l.year ?? "–"}</td>
                <td className="px-4 py-3">{l.length ? `${l.length} m` : "–"}</td>
                <td className="px-4 py-3">{l.sleepingSpots ?? "–"}</td>
                <td className="px-4 py-3 font-medium">{kr(l.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Frame>
  );
}

/** Interna nyckeltal ur lagret — den typ av data ingen annan sajt kan kopiera. */
function FleetStats({ listings }: { listings: FyndanuListing[] }) {
  const priced = listings.filter((l) => l.price && l.price > 20000);
  if (priced.length < 4) return null;
  const prices = priced.map((l) => l.price as number).sort((a, b) => a - b);
  const median = prices[Math.floor(prices.length / 2)];
  const years = priced.map((l) => l.year).filter((y): y is number => !!y);
  const avgYear = years.length ? Math.round(years.reduce((a, b) => a + b, 0) / years.length) : null;
  const motorhomes = listings.filter((l) => l.category === "husbil").length;
  const caravans = listings.filter((l) => l.category === "husvagn").length;

  const stats: Array<[string, string]> = [
    ["Fordon i lager", String(listings.length)],
    ["Medianpris", kr(median)],
    ["Prisspann", `${kr(prices[0])} – ${kr(prices[prices.length - 1])}`],
    ...(avgYear ? ([["Snittårsmodell", String(avgYear)]] as Array<[string, string]>) : []),
    ["Husbilar / husvagnar", `${motorhomes} / ${caravans}`],
  ];

  return (
    <Frame
      eyebrow="Intern data · Kalix"
      title="Så ser vårt lager ut i dag"
      footnote="Siffrorna räknas fram live ur vårt egna lager varje gång sidan laddas."
    >
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map(([label, value]) => (
          <div key={label} className="rounded-2xl bg-secondary/40 p-4">
            <dt className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Database size={12} /> {label}
            </dt>
            <dd className="mt-1 font-display text-xl">{value}</dd>
          </div>
        ))}
      </dl>
    </Frame>
  );
}

/** Egen bild ur lagret med redaktionell bildtext — våra fordon, våra ögon. */
function PhotoNote({ listings, seed }: { listings: FyndanuListing[]; seed: number }) {
  const withImage = listings.filter((l) => l.image && l.image.startsWith("http"));
  if (!withImage.length) return null;
  const pick = withImage[seed % withImage.length];

  return (
    <figure className="not-prose my-10 overflow-hidden rounded-3xl border border-border bg-card">
      <img
        src={pick.image}
        alt={pick.title}
        loading="lazy"
        className="aspect-[16/9] w-full object-cover"
      />
      <figcaption className="flex flex-col gap-2 p-5 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider">
          <Camera size={12} /> Egen bild · vår anläggning i Kalix
        </span>
        <span>
          {pick.title}
          {pick.year ? ` (${pick.year})` : ""} — fotad hos oss. Så här ser vi på fordonen när vi
          fuktmäter, provkör och gasoltestar innan de får ligga ute till försäljning.
        </span>
        <Link
          to="/fordon/$id"
          params={{ id: vehicleSlug(pick) }}
          className="text-accent hover:underline"
        >
          Se fordonet och alla bilder →
        </Link>
      </figcaption>
    </figure>
  );
}

/** Fyndanu-tips — bara i artiklar där köp/sälj faktiskt är ämnet. */
function FyndanuTip() {
  return (
    <aside className="not-prose my-10 rounded-3xl border border-accent/30 bg-accent/5 p-5 md:p-7">
      <p className="eyebrow">Tips från oss</p>
      <h3 className="mt-1 font-display text-xl md:text-2xl">
        Leta brett innan du bestämmer dig
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        Vi lägger ut våra fordon på <strong>Fyndanu.se</strong> — en svensk marknadsplats med
        verifierade handlare och modererade annonser. Där ser du både vårt lager och privatannonser
        på samma ställe, vilket gör det enklare att jämföra prisnivå innan du åker och tittar.
      </p>
      <a
        href="https://fyndanu.se"
        target="_blank"
        rel="noopener"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
      >
        Jämför fordon på Fyndanu.se <ExternalLink size={14} />
      </a>
    </aside>
  );
}

export function KbBlockView({
  block,
  listings,
  seed,
}: {
  block: KbBlock;
  listings: FyndanuListing[];
  seed: number;
}) {
  switch (block.kind) {
    case "price-compare":
      return <PriceCompare listings={listings} />;
    case "fleet-stats":
      return <FleetStats listings={listings} />;
    case "calc-tco":
      return (
        <div className="not-prose my-10">
          <BlogTcoCalculator />
        </div>
      );
    case "calc-monthly":
      return <KbMonthlyCalc />;
    case "calc-payload":
      return (
        <KbPayloadCalc
          presets={listings
            .filter((l) => l.totalWeight && l.serviceWeight)
            .slice(0, 4)
            .map((l) => ({
              label: l.title.slice(0, 28),
              total: l.totalWeight as number,
              service: l.serviceWeight as number,
            }))}
        />
      );
    case "photo-note":
      return <PhotoNote listings={listings} seed={seed} />;
    case "fyndanu-tip":
      return <FyndanuTip />;
    default:
      return null;
  }
}
